/**
 * Display helpers for contract refs shown next to a carrier logo.
 *
 * Stored refs often repeat the SCAC (`MSK-2025-100`). The UI always prefixes
 * the carrier logo / label, so we drop that leading code and keep the agent
 * contract token.
 */

const CARRIER_ALIAS_GROUPS: readonly (readonly string[])[] = [
  ["MSK", "MAERSK", "MAEU"],
  ["MSC", "MSCU", "MEDU"],
  ["ZIM", "ZIMU"],
  ["ONE", "ONEY"],
  ["EMC", "EGLV", "EISU"],
  ["YML", "YMLU"],
  ["OOCL"],
  ["HLC", "HLCU", "HPL", "HAPAG", "HAPAG-LLOYD"],
  ["CMA", "CMDU", "CMA-CGM"],
  ["COSCO", "COSU"],
  ["HMM", "HDMU"],
];

const ALL_CARRIER_PREFIXES = [...CARRIER_ALIAS_GROUPS]
  .flat()
  .sort((a, b) => b.length - a.length);

const ALIASES_BY_CODE = new Map<string, string[]>();
for (const group of CARRIER_ALIAS_GROUPS) {
  for (const code of group) {
    ALIASES_BY_CODE.set(code, [...group]);
  }
}

function prefixCandidates(carrier?: string | null): string[] {
  const extra = carrier?.trim().toUpperCase();
  const aliases = extra ? (ALIASES_BY_CODE.get(extra) ?? [extra]) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of [...aliases, ...ALL_CARRIER_PREFIXES]) {
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out.sort((a, b) => b.length - a.length);
}

/**
 * Strip a leading carrier SCAC / alias from a contract ref.
 * `MSK-2025-100` → `2025-100`. Leaves agent-prefixed refs (`TST-ZIM-2026`)
 * and already-bare refs unchanged.
 */
export function normalizeContractRef(
  contractRef?: string | null,
  carrier?: string | null,
): string {
  const trimmed = contractRef?.trim() ?? "";
  if (!trimmed) return "";

  const upper = trimmed.toUpperCase();
  for (const prefix of prefixCandidates(carrier)) {
    if (!upper.startsWith(prefix) || upper.length <= prefix.length) continue;
    const sep = trimmed.charAt(prefix.length);
    if (sep !== "-" && sep !== "_" && sep !== "/" && sep !== " ") continue;
    const rest = trimmed.slice(prefix.length + 1).replace(/^[-_/\s]+/, "");
    if (rest) return rest;
  }
  return trimmed;
}

/**
 * Contract token for display. Appends the rate-card named account when it
 * adds information (`TOPTRANS` stays `TOPTRANS`; a numeric ref becomes
 * `7110381721 (TOPTRANS)`).
 */
export function formatContractRef(
  contractRef?: string | null,
  namedAccount?: string | null,
  carrier?: string | null,
): string {
  const raw = contractRef?.trim() ?? "";
  if (!raw) return "";
  const ref = normalizeContractRef(raw, carrier) || raw;
  const account = namedAccount?.trim();
  if (
    account &&
    account.toLowerCase() !== ref.toLowerCase() &&
    account.toLowerCase() !== raw.toLowerCase()
  ) {
    return `${ref} (${account})`;
  }
  return ref;
}
